import { useState } from 'react';
import { useIdentityPrefill } from '../../hooks/useIdentityPrefill';
import styles from './FeedbackSection.module.css';

const SUPPORT_EMAIL = 'support@repbuddy.io';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// The founding-launch features members can rate. `label` is what gets emailed to the team.
const FEATURES = [
  { key: 'lead_hunting',       label: 'Lead hunting',            desc: 'Finding verified, net-new contacts' },
  { key: 'email_verification', label: 'Email verification',      desc: 'Checking addresses in real time' },
  { key: 'company_enrichment', label: 'Company enrichment',      desc: 'Filling in company details' },
  { key: 'website_lookups',    label: 'Website lookups',         desc: 'Resolving companies to the right site' },
  { key: 'ai_drafting',        label: 'AI email drafting & chat', desc: 'Writing outreach and the assistant' },
];

// 1–5 star control. Clicking the current value again clears it back to 0 (unrated).
function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div className={styles.stars} role="radiogroup" aria-label={`${label} rating`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`${styles.star} ${(hover || value) >= n ? styles.starOn : ''}`}
          onClick={() => onChange(n === value ? 0 : n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          aria-pressed={value === n}
        >
          ★
        </button>
      ))}
      <span className={styles.starValue}>{value ? `${value}/5` : ''}</span>
    </div>
  );
}

export default function FeedbackSection() {
  const [form, setForm] = useState({ name: '', email: '', likeMost: '', improve: '' });
  const [ratings, setRatings] = useState({}); // { [feature.key]: 1-5 }
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Signed-in members get their name / email filled in, still editable.
  useIdentityPrefill(setForm);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function setRating(key, value) {
    setRatings((r) => ({ ...r, [key]: value }));
    if (errors.overall) setErrors((e) => ({ ...e, overall: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Tell us your name.';
    if (!form.email.trim()) e.email = 'We need an email to follow up.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'That email doesn’t look right.';
    const anyRating = FEATURES.some((f) => ratings[f.key] > 0);
    if (!anyRating && !form.likeMost.trim() && !form.improve.trim()) {
      e.overall = 'Rate at least one feature or leave a comment before submitting.';
    }
    return e;
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSending(true);
    setSubmitError('');
    try {
      const ratingList = FEATURES
        .filter((f) => ratings[f.key] > 0)
        .map((f) => ({ feature: f.label, stars: ratings[f.key] }));

      const res = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          ratings: ratingList,
          likeMost: form.likeMost.trim(),
          improve: form.improve.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong.');
      }
      setSent(true);
    } catch (err) {
      setSubmitError(
        err.message === 'Failed to fetch'
          ? `We couldn’t reach the server. Please try again, or email ${SUPPORT_EMAIL} directly.`
          : err.message
      );
    } finally {
      setSending(false);
    }
  }

  function resetForm() {
    setForm({ name: '', email: '', likeMost: '', improve: '' });
    setRatings({});
    setErrors({});
    setSubmitError('');
    setSent(false);
  }

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* Aside */}
        <aside className={styles.aside}>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>Why we ask</span>
            <p className={styles.asideText}>
              You're one of the first to use RepBuddy. Your ratings and notes go straight to
              the team and directly shape what we build next.
            </p>
          </div>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>Prefer email?</span>
            <a className={styles.email} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>How it works</span>
            <ul className={styles.topicList}>
              <li><strong>Rate</strong> the features you've used, 1–5 stars.</li>
              <li><strong>Tell us</strong> what you like and what to improve.</li>
              <li><strong>Skip</strong> anything you haven't tried yet.</li>
            </ul>
          </div>
        </aside>

        {/* Form */}
        <div className={styles.formWrap}>
          {sent ? (
            <div className={styles.success}>
              <span className={styles.successMark} aria-hidden="true">✓</span>
              <h2 className={styles.successHeading}>Thank you for the feedback.</h2>
              <p className={styles.successText}>
                We got it — every note is read by the team. If you'd like to add more, you
                can also reach us at{' '}
                <a className={styles.inlineLink} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
              <button className={styles.resetBtn} onClick={resetForm}>Send more feedback</button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.grid2}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Name</span>
                  <input
                    className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Jordan Rivera"
                  />
                  {errors.name && <span className={styles.error}>{errors.name}</span>}
                </label>

                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Email</span>
                  <input
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@company.com"
                  />
                  {errors.email && <span className={styles.error}>{errors.email}</span>}
                </label>
              </div>

              {/* Feature ratings */}
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Rate the features</span>
                <div className={styles.ratingList}>
                  {FEATURES.map((f) => (
                    <div key={f.key} className={styles.ratingRow}>
                      <div className={styles.ratingMeta}>
                        <span className={styles.ratingName}>{f.label}</span>
                        <span className={styles.ratingDesc}>{f.desc}</span>
                      </div>
                      <StarRating value={ratings[f.key] || 0} onChange={(v) => setRating(f.key, v)} label={f.label} />
                    </div>
                  ))}
                </div>
              </div>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>What do you like most?</span>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={form.likeMost}
                  onChange={(e) => update('likeMost', e.target.value)}
                  placeholder="What's working well for you?"
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>What needs improvement?</span>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={form.improve}
                  onChange={(e) => update('improve', e.target.value)}
                  placeholder="What would make RepBuddy better?"
                />
              </label>

              {errors.overall && <p className={styles.error} role="alert">{errors.overall}</p>}
              {submitError && <p className={styles.error} role="alert">{submitError}</p>}
              <button className={styles.submit} type="submit" disabled={sending}>
                {sending ? 'Sending…' : 'Submit feedback'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
